<template>
  <div class="flex flex-col flex-1">
    <!-- Top-level items (no header) -->
    <nav class="px-2 pb-2 space-y-0.5">
      <template v-for="item in topLevelItems" :key="item.name">
        <router-link
          :to="item.to"
          class="flex items-center px-2 py-1.5 rounded-md group"
          :class="[
            isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
            'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
          ]"
          @click="$emit('close')"
        >
          <component
            :is="item.icon"
            class="h-4 w-4 mr-2"
            :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
            aria-hidden="true"
          />
          <span>{{ item.name }}</span>
        </router-link>
      </template>
    </nav>

    <!-- Navigation groups with headers + bottom items -->
    <nav class="flex-1 px-2 pb-4 space-y-6 mt-2">
      <template v-for="group in navigationGroups" :key="group.key">
        <div>
          <p class="px-2 mb-1 text-xs font-extralight text-gray-800 uppercase tracking-wider">
            {{ group.label }}
          </p>
          <div class="space-y-0.5">
            <template v-for="item in group.items" :key="item.name">
              <div>
                <!-- Parent navigation item with children -->
                <div
                  v-if="item.children"
                  @click="toggleItem(item.name)"
                  class="flex items-center justify-between cursor-pointer px-2 py-1.5 rounded-md group"
                  :class="[
                    isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                    'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                  ]"
                >
                  <div class="flex items-center space-x-2">
                    <component
                      :is="item.icon"
                      class="h-4 w-4"
                      :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                      aria-hidden="true"
                    />
                    <span>{{ item.name }}</span>
                    <span
                      v-if="item.badge"
                      data-test="menu-badge"
                      class="min-w-4 rounded-full bg-red-500 px-1 text-center text-xs font-medium text-white"
                    >
                      {{ item.badge }}
                    </span>
                  </div>
                  <component
                    :is="isExpanded(item.name) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    class="h-4 w-4 text-gray-400 transition-transform duration-200"
                  />
                </div>

                <!-- Simple navigation item -->
                <router-link
                  v-else
                  :to="item.to"
                  class="flex items-center px-2 py-1.5 rounded-md group"
                  :class="[
                    isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                    'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
                  ]"
                  @click="$emit('close')"
                >
                  <component
                    :is="item.icon"
                    class="h-4 w-4 mr-2"
                    :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                    aria-hidden="true"
                  />
                  <span>{{ item.name }}</span>
                </router-link>

                <!-- Children navigation items -->
                <transition
                  @before-enter="beforeEnter"
                  @enter="enter"
                  @after-enter="afterEnter"
                  @before-leave="beforeLeave"
                  @leave="leave"
                  @after-leave="afterLeave"
                >
                  <div
                    v-if="item.children && isExpanded(item.name)"
                    class="ml-5 border-l border-gray-200 pl-2 overflow-hidden"
                  >
                    <router-link
                      v-for="child in item.children"
                      :key="child.name"
                      :to="child.to"
                      class="flex items-center px-3 py-1.5 text-sm rounded-md"
                      :class="[
                        isActiveRoute(child)
                          ? 'bg-primary-50 text-primary-dark font-medium'
                          : 'text-gray-500 hover:bg-gray-100',
                      ]"
                      @click="$emit('close')"
                    >
                      {{ child.name }}
                    </router-link>
                  </div>
                </transition>
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- Bottom items (no header, same spacing as groups) -->
      <div>
        <div class="space-y-0.5">
          <template v-for="item in bottomItems" :key="item.name">
            <router-link
              :to="item.to"
              class="flex items-center px-2 py-1.5 rounded-md group"
              :class="[
                isActiveRoute(item) ? 'text-primary-dark font-medium' : '',
                'group flex items-center px-2 py-1.5 text-sm hover:bg-gray-200 rounded-md text-gray-500',
              ]"
              @click="$emit('close')"
            >
              <component
                :is="item.icon"
                class="h-4 w-4 mr-2"
                :class="isActiveRoute(item) ? 'text-primary-dark' : 'text-gray-400'"
                aria-hidden="true"
              />
              <span>{{ item.name }}</span>
            </router-link>
          </template>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import {
  HomeIcon,
  FireIcon,
  ExclamationCircleIcon,
  ShoppingBagIcon,
  CurrencyEuroIcon,
  AnnotationIcon,
  ArchiveIcon,
  DocumentDownloadIcon,
  DocumentIcon,
  TagIcon,
  StarIcon,
  BanIcon,
  ChartBarIcon,
  FolderOpenIcon,
  UserCircleIcon,
  UserGroupIcon,
  OfficeBuildingIcon,
  PresentationChartLineIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MailIcon,
} from "@heroicons/vue/outline";

export default {
  components: {
    HomeIcon,
    FireIcon,
    ExclamationCircleIcon,
    ShoppingBagIcon,
    CurrencyEuroIcon,
    AnnotationIcon,
    ArchiveIcon,
    DocumentDownloadIcon,
    DocumentIcon,
    TagIcon,
    StarIcon,
    BanIcon,
    ChartBarIcon,
    FolderOpenIcon,
    UserCircleIcon,
    UserGroupIcon,
    OfficeBuildingIcon,
    PresentationChartLineIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    MailIcon,
  },

  data() {
    return {
      expandedItems: new Set(),
    };
  },

  computed: {
    unseenClaimsCount() {
      return this.$store.state.claims.inboxCounts.unseen;
    },

    topLevelItems() {
      return [
        { name: "Accueil", to: "/", icon: HomeIcon },
        { name: "Pilotage", to: "/analytics/retail", icon: PresentationChartLineIcon },
      ];
    },

    navigationGroups() {
      return [
        {
          key: "gestion",
          label: "Gestion",
          items: [
            { name: "Contrats", to: "/plan-sales", icon: FireIcon },
            {
              name: "Sinistres",
              to: "/claims",
              icon: ExclamationCircleIcon,
              badge: this.unseenClaimsCount,
              children: [
                { name: "Mes dossiers", to: "/claims/mine" },
                { name: "Non assignés", to: "/claims/unassigned" },
                { name: "Tous", to: "/claims", exact: true },
              ],
            },
            { name: "Produits", to: "/products", icon: ShoppingBagIcon },
            { name: "Leads", to: "/leads", icon: CurrencyEuroIcon },
            { name: "Réclamations", to: "/complaints", icon: AnnotationIcon },
          ],
        },
        {
          key: "marchands",
          label: "Marchands",
          items: [
            { name: "Points de vente", to: "/stores", icon: OfficeBuildingIcon },
            { name: "Organisations", to: "/organizations", icon: UserGroupIcon },
            { name: "Utilisateurs", to: "/merchant_profiles", icon: UserCircleIcon },
            { name: "Onboardings", to: "/onboardings", icon: FolderOpenIcon },
          ],
        },
        {
          key: "finance",
          label: "Finance",
          items: [
            {
              name: "Facturation",
              to: "/billing/invoices",
              icon: ArchiveIcon,
              children: [
                { name: "Commissions", to: "/billing/invoices" },
                { name: "Sinistres", to: "/billing/claim_invoices" },
              ],
            },
            { name: "Rapports", to: "/report", icon: DocumentDownloadIcon },
          ],
        },
      ];
    },

    bottomItems() {
      return [
        { name: "Offres", to: "/contract-distributions", icon: DocumentIcon },
        { name: "Promotions", to: "/discounts", icon: TagIcon },
        { name: "Challenges", to: "/challenges", icon: StarIcon },
        { name: "Produits irréparables", to: "/unfixeable-claim-products", icon: BanIcon },
        { name: "Suivi challenge", to: "/challenges_progress", icon: ChartBarIcon },
        { name: "Email Logs", to: "/email-logs", icon: MailIcon },
      ];
    },
  },

  watch: {
    "$route.path": {
      immediate: true,
      handler(path) {
        if (path === "/claims" || path.startsWith("/claims/")) this.expandedItems.add("Sinistres");
      },
    },
  },

  methods: {
    isActiveRoute(item) {
      if (item.to === this.$route.path) return true;
      if (item.exact) return false;
      if (this.$route.path.startsWith(item.to + "/")) return true;
      if (item.children) return item.children.some((child) => this.isActiveRoute(child));
      return false;
    },
    toggleItem(itemName) {
      if (this.expandedItems.has(itemName)) {
        this.expandedItems.delete(itemName);
      } else {
        this.expandedItems.clear();
        this.expandedItems.add(itemName);
      }
    },
    isExpanded(itemName) {
      return this.expandedItems.has(itemName);
    },
    // Transition hooks for smooth slide
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
    },
    enter(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },
    leave(el) {
      el.style.transition = "height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s";
      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.height = "auto";
    },
  },
};
</script>
